import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import type { LottieComponentProps } from "lottie-react";
import { loadLottieLib } from "@/extension/utils/lazy-lib";

/**
 * Stateful lazy boundary over lottie-react's <Lottie/> component.
 *
 * lottie-react's only heavy dependency is lottie-web (~250KB, the Bodymovin
 * animation engine). Every <Lottie/> in the app is a decorative empty-state /
 * loader / checkmark that appears only behind a user interaction (an empty
 * tasks/contact list, the trial-activate modal, a board loader, …), so shipping
 * lottie-web in app.js means those bytes are parsed at content-script startup
 * for nothing. This boundary loads the Lottie component on demand from the
 * lazy/lottie.js WAR bundle (which shares app.js's React via the __BT_REACT__
 * bridge — see src/extension/utils/lazy-lib.ts loadLottieLib).
 *
 * Why a stateful wrapper instead of React.lazy + Suspense (as the calendar /
 * date-range splits use): lottie animations render in MANY scattered, always-
 * mounted spots (each empty-state view, each modal) with no shared Suspense
 * boundary, and each caller already sizes the animation via `style`. A stateful
 * loader lets every instance paint a correctly-sized placeholder box and swap in
 * the real animation once the shared (memoized) loader resolves — zero layout
 * shift, no suspense flash, no risk of suspending an unexpected ancestor. It
 * reuses the exact same lazy infra as the world-flags split.
 *
 * The `animationData` JSON stays imported by each consumer in app.js (it is
 * data, not the engine) and is forwarded through as-is. The loaded component is
 * cached module-side so only the first mounted animation pays the load; every
 * later one renders synchronously.
 */

export type LazyLottieProps = LottieComponentProps;

// Shared across every LazyLottie instance in this document: once the first
// animation resolves the component, later ones render it synchronously.
let cachedLottieComponent: any = null;

const LazyLottie: React.FC<LazyLottieProps> = (props) => {
  // NB: pass a lazy INITIALIZER (`() => cachedLottieComponent`), never the value
  // directly. When the cache is already warm, `cachedLottieComponent` is the Lottie
  // *function component*; `useState(fn)` treats a function as a lazy initializer and
  // CALLS it — i.e. `Lottie()` with no props → `undefined.style` → crash that blanks
  // the whole app. The initializer returns the component without invoking it. (Same
  // reason the setter below uses `() => Lottie`.)
  const [LottieComponent, setLottieComponent] = useState<any>(() => cachedLottieComponent);

  useEffect(() => {
    if (cachedLottieComponent) return;
    let mounted = true;
    loadLottieLib()
      .then((Lottie) => {
        cachedLottieComponent = Lottie;
        // Store the component itself via the functional updater so React doesn't
        // treat the function component as a lazy state initializer.
        if (mounted) setLottieComponent(() => Lottie);
      })
      .catch(() => {
        /* loadLottieLib already logs + guards its own fallback */
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!LottieComponent) {
    // Sized placeholder so the animation popping in causes no layout shift. Uses
    // the caller's own style (which carries width/height at every call site) so
    // it occupies the exact animation footprint.
    const style = (props as { style?: CSSProperties }).style;
    return (
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          ...style,
        }}
      />
    );
  }

  return <LottieComponent {...props} />;
};

export default LazyLottie;
